import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Modal, Form, Alert, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { getClientSms, sendClientSms } from "state/roadmapsCompanion/actions";
import { getClientSummary } from "app/companion/pages/delivery/clientSms";

const DEFAULT_MAX_LENGTH = 320;

/**
 * Fenêtre d'envoi du SMS d'annonce de départ.
 *
 * Toujours facultative : « Ne pas envoyer » ferme sans rien faire, et rien d'autre dans
 * le compagnon n'attend cet envoi. Le texte proposé vient de l'API et reste modifiable
 * — le livreur ajuste en général le créneau d'arrivée.
 *
 * `roadmapDelivery` n'est pas forcément l'arrêt affiché : à la confirmation d'une
 * livraison, c'est celui du client suivant. D'où le rappel du destinataire en tête.
 *
 * `resend` n'est passé que par le bouton de la feuille de livraison : il autorise un
 * envoi alors que le client a déjà été prévenu.
 */
const ClientSmsModal = ({ show, onClose, roadmapDelivery, title, resend = false }) => {
  const dispatch = useDispatch();
  const [state, setState] = useState(null);
  const [content, setContent] = useState("");
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (show && roadmapDelivery && !state) {
      dispatch(getClientSms(roadmapDelivery.id)).then((resp) => {
        if (resp?.error || resp?.success === false) return;
        setState(resp);
        setContent(resp?.content || "");
      });
    }
  }, [show, state, roadmapDelivery, dispatch]);

  useEffect(() => {
    if (!show) {
      setState(null);
      setContent("");
    }
  }, [show]);

  if (!roadmapDelivery) return null;

  const maxLength = state?.max_length || DEFAULT_MAX_LENGTH;
  const isLoading = show && !state;
  const client = getClientSummary(roadmapDelivery);
  // Le verrou « déjà prévenu » ne s'applique pas au renvoi manuel.
  const isBlocked = resend ? state?.can_resend === false : state?.eligible === false;
  const alreadySent = Boolean(state?.sent_at);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error("Le message ne peut pas être vide");
      return;
    }
    setIsSending(true);
    const resp = await dispatch(sendClientSms(roadmapDelivery.id, content.trim(), resend));
    setIsSending(false);
    // Un échec (400) est déjà signalé par l'intercepteur axios : pas de second toast.
    if (resp?.error || resp?.success === false) return;
    toast.success(resend && alreadySent ? "SMS renvoyé au client" : "SMS envoyé au client");
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{title || "Prévenir le client"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {isLoading ? (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" /> Préparation du message…
            </div>
          ) : (
            <>
              {isBlocked && (
                <Alert variant="warning">
                  Envoi impossible pour cette livraison.
                </Alert>
              )}
              {!isBlocked && alreadySent && (
                <Alert variant="warning" className="py-2">
                  <small>
                    Ce client a <strong>déjà été prévenu</strong>
                    {state?.sent_count > 1 && ` (${state.sent_count} envois)`} le{" "}
                    {moment(state.sent_at).format("DD/MM [à] HH[h]mm")}. Un nouvel envoi
                    lui fera un second SMS.
                  </small>
                </Alert>
              )}
              {state?.test_mode && (
                <Alert variant="danger" className="py-2">
                  <small>
                    <strong>Environnement de test.</strong> Le SMS ne partira pas chez le
                    client ({state?.client_phone}) mais sur le numéro de test{" "}
                    <strong>{state?.phone}</strong>.
                  </small>
                </Alert>
              )}
              <Alert variant="info" className="py-2">
                <small>
                  {client?.company && (
                    <>
                      Société : <strong>{client.company}</strong>
                      <br />
                    </>
                  )}
                  {client?.contact && (
                    <>
                      Contact : <strong>{client.contact}</strong>
                      <br />
                    </>
                  )}
                  {client?.numbering && (
                    <>
                      Prestation : <strong>{client.numbering}</strong>
                      <br />
                    </>
                  )}
                  Envoi au <strong>{state?.phone}</strong>, de la part de LEMOULIN.
                  Relisez le créneau avant d'envoyer.
                </small>
              </Alert>
              <Form.Group>
                <Form.Label>Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={content}
                  maxLength={maxLength}
                  onChange={(e) => setContent(e.target.value)}
                />
                <Form.Text className="text-muted text-right">
                  {content.length} / {maxLength} caractères
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Ne pas envoyer
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isSending || isLoading || isBlocked}
          >
            {isSending ? "Envoi…" : alreadySent ? "Renvoyer le SMS" : "Envoyer le SMS"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ClientSmsModal;
